<template>
  <c-list-page
    :filter-groups="filterGroups"
    :pagination="pagination"
    :items="products"
    :loading="productStore.runningRequests.apiFetchProductList"
    :header-config="pageHeaderConfig"
    :no-items-found-message="$t('l-articles-list.no-articles-found')"
    @update-filter="onFilterChange"
    @reset-filter="onResetFilter"
    @page-change="onPageChange"
  >
    <template #breadcrumb>
      <c-breadcrumb :class="b('breadcrumb')" />
    </template>
    <template #header>
      <c-page-header :config="pageHeaderConfig" />
    </template>
    <template #filters>
      <c-filters
        :filter-groups="filterGroups"
        @update-filter="onFilterChange"
      />
    </template>
    <template #top-element>
      <div :class="b('add-filter-result-to-favorites')">
        <c-add-filter-result-to-favorites :product-ids="productStore.filterResultFavoritesIds" />
      </div>
    </template>
    <template #items="{ items }">
      <c-teaser-tile
        v-for="product in items"
        :id="product.id"
        :key="product.id"
        :url="product.relativeUrl"
        :heading="product.name"
        :description="product.description"
        :image="product.imageMain"
        :text-image-overlay="product.textImageOverlay"
        :label="product.availabilityLabel"
        :reference-code="product.sku"
        show-read-more
        show-favorites-toggle
      />
    </template>
  </c-list-page>
</template>

<script lang="ts">
  import { PropType, defineComponent } from 'vue';
  import useProductStore from '@/stores/product';
  import cAddFilterResultToFavorites from '@/components/c-add-filter-result-to-favorites.vue';
  import cBreadcrumb from '@/components/c-breadcrumb.vue';
  import cFilters from '@/components/c-filters.vue';
  import cListPage from '@/components/c-list-page.vue';
  import cPageHeader, { PageHeaderConfig } from '@/components/c-page-header.vue';
  import cTeaserTile from '@/components/c-teaser-tile.vue';
  import { getFilterGroupsWithSelection, resetAllFilters, updateFilterGroupsForFe } from '@/helpers/filters';
  import { generateFilterQuery } from '@/helpers/generateFilterQuery';
  import { initializeFilterQueryAndPagination } from '@/helpers/initializeFilterQueryAndPagination';
  import { Pagination } from '@/helpers/pagination';
  import { ArticlesListCategory as Category } from '@/types/category';
  import { FilterGroup } from '@/types/filter';
  import { ElasticSearchProductEntry } from '@/types/product';

  type Setup = {
    productStore: ReturnType<typeof useProductStore>;
  };

  type Data = {
    filterGroups: FilterGroup[];
    pagination: Pagination;
    filterQuery: string;
    products: ElasticSearchProductEntry[];
  };

  /**
   * Renders an article list page.
   */
  export default defineComponent({
    name: 'l-articles-list',

    components: {
      cListPage,
      cBreadcrumb,
      cPageHeader,
      cFilters,
      cTeaserTile,
      cAddFilterResultToFavorites,
    },

    props: {
      /**
       * The category, needed for:
       * - building page header config
       * - fetching products list
       */
      category: {
        type: Object as PropType<Category>,
        required: true,
      },

      /**
       * The number of list items per page
       */
      numItemsPerPage: {
        type: Number,
        default: 24,
      },
    },
    // emits: {},

    setup(): Setup {
      return {
        productStore: useProductStore(),
      };
    },

    data(): Data {
      return {
        filterQuery: '',
        pagination: new Pagination({ numItemsPerPage: this.numItemsPerPage }),
        filterGroups: [],
        products: [],
      };
    },

    computed: {
      filterGroupsWithSelection(): FilterGroup[] {
        return getFilterGroupsWithSelection(this.filterGroups);
      },

      pageHeaderConfig(): PageHeaderConfig {
        return {
          heading: this.category.name || this.$t('l-articles-list.heading'),
          description: this.category.description,
          ctaPrimary: this.category.ctaPrimary,
          ctaSecondary: this.category.ctaSecondary,
        };
      },
    },
    // watch: {},

    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    mounted() {
      const url = new URL(window.location.href);

      const { filterQuery, pagination } = initializeFilterQueryAndPagination(url);

      this.filterQuery = filterQuery;

      if (pagination.current) {
        this.pagination.current = pagination.current;
      }

      this.updateList();
    },

    // beforeUpdate() {},
    // updated() {},
    // activated() {},
    // deactivated() {},
    // beforeUnmount() {},
    // unmounted() {},

    methods: {
      onFilterChange() {
        this.setUrlParams(1);
        this.updateList();
      },

      onResetFilter(): void {
        resetAllFilters(this.filterGroups);
        this.setUrlParams(1, true);
        this.updateList();
      },

      setUrlParams(paginationCurrentPageOverwrite?: number, clearAllFilters: boolean = false): void {
        const url = new URL(window.location.href);

        this.filterQuery = generateFilterQuery(
          url,
          this.filterGroupsWithSelection,
          { ...this.pagination, current: paginationCurrentPageOverwrite || this.pagination.current },
          clearAllFilters,
        );

        window.history.replaceState({}, '', url);
      },

      onPageChange(page: number) {
        this.pagination.current = page;
        this.setUrlParams();
        this.updateList();
      },

      updateList(): void {
        this.productStore.apiFetchProductList(this.filterQuery, this.category.id, this.pagination).then((response) => {
          if (
            !response.data.length &&
            response.pagination.pageCount > 0 &&
            this.pagination.current > response.pagination.pageCount
          ) {
            this.onPageChange(1);

            return;
          }

          this.products = response.data;
          this.filterGroups = updateFilterGroupsForFe(response.filterGroups);
          this.pagination.update(response.pagination);
        });
      },
    },
  });
</script>

<style lang="scss">
  @use 'sass:map';
  @use '@/setup/scss/mixins';
  @use '@/setup/scss/variables';

  .l-articles-list {
    &__breadcrumb {
      margin-bottom: 3rem;
    }

    &__add-filter-result-to-favorites {
      width: 100%;
      position: absolute;
      top: -2rem;
      display: flex;
      justify-content: flex-end;
    }
  }

  .c-list-page__list-container {
    margin-top: 2rem;

    @include mixins.media(md) {
      margin-top: 0;
    }
  }
</style>
